import React from "react";
import PropTypes from "prop-types";
import { Route, Switch, Link, Redirect, withRouter } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Divider from "@material-ui/core/Divider";
import ListSubheader from "@material-ui/core/ListSubheader";

import ShoppingCartIcon from "@material-ui/icons/ShoppingCart";
import DraftIcon from "@material-ui/icons/Assignment";
import DashboardIcon from "@material-ui/icons/Dashboard";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import DescriptionIcon from "@material-ui/icons/Description";
import ReceiptIcon from "@material-ui/icons/Receipt";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import SettingsIcon from "@material-ui/icons/Settings";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PaymentIcon from "@material-ui/icons/Payment";
import SecurityIcon from "@material-ui/icons/Security";
import DirectionsBoatIcon from "@material-ui/icons/DirectionsBoat";
import PlaylistAddCheckIcon from "@material-ui/icons/PlaylistAddCheck";
import EmailIcon from "@material-ui/icons/Email";
import SearchIcon from "@material-ui/icons/Search";
import TableChartIcon from "@material-ui/icons/TableChart";

import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import utils from "../../universal/utils";

// Import the existing order management components
import OrderList from "../admin-page/OrderList";
import DraftOrdersManager from "./DraftOrdersManager";
import DraftOrderDetailPage from "./DraftOrderDetailPage";
import WizardAnalytics from "./WizardAnalytics";
import AdminAnalyticsDashboard from "./AdminAnalyticsDashboard";
import PricingAgentPage from "./PricingAgentPage";
import EceCertificatesManager from "./EceCertificatesManager";
import EceInvoicesManager from "./EceInvoicesManager";
import CouponsManager from "./CouponsManager";
import LimitedTimeOffersManager from "./LimitedTimeOffersManager";
import HomepageSalesPromoManager from "./HomepageSalesPromoManager";
import TireSetsManager from "./TireSetsManager";
import GeorgiaPricingManager from "./GeorgiaPricingManager";
import BrandLogoManager from "./BrandLogoManager";
import ProductAnalyticsPage from "./ProductAnalyticsPage";
import SalesStockSummary from "./SalesStockSummary";
import PaymentLinksManager from "./PaymentLinksManager";
import CreditGuardOrderAuditManager from "./CreditGuardOrderAuditManager";
import OnDemandOrdersManager from "./OnDemandOrdersManager";
import GoogleSheetsSyncManager from "./GoogleSheetsSyncManager";
import OrderTopUpManager from "./OrderTopUpManager";
import EuContainerModeManager from "./EuContainerModeManager";
import MailTransportManager from "./MailTransportManager";
import DbHealthMonitor from "./DbHealthMonitor";
import CatalogSearch from "./CatalogSearch";
import PerformanceReportsPage from "./PerformanceReportsPage";

const styles = (theme) => ({
  root: {
    marginTop: 33,
    marginLeft: "auto",
    marginRight: "auto",
    minHeight: "80vh",
  },
  adminAppBar: {
    backgroundColor: "#1976d2", // Material Design blue instead of orange
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  toolbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 64,
  },
  title: {
    color: "white",
    fontWeight: 600,
  },
  navigation: {
    flexGrow: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  navButton: {
    color: "white",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    borderRadius: 6,
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  navButtonActive: {
    color: "white",
    fontWeight: 700,
    backgroundColor: "rgba(255,255,255,0.14)",
  },
  userSection: {
    display: "flex",
    alignItems: "center",
    color: "white",
  },
  logoutButton: {
    marginLeft: theme.spacing(2),
    color: "white",
    borderColor: "white",
  },
  content: {
    padding: theme.spacing(4),
    minHeight: "calc(100vh - 150px)",
  },
  headline: {
    fontSize: "24pt",
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary,
  },
  welcomeCard: {
    padding: theme.spacing(4),
    textAlign: "center",
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  "@media (min-width: 1024px)": {
    root: {
      marginTop: 80,
      width: "95%",
      maxWidth: 1400,
    },
  },
});

const hebrewLabels = {
  title: "מערכת ניהול מתקדמת",
  navigation: {
    dashboard: "לוח בקרה",
    orders: "ניהול הזמנות",
    draftOrders: "הזמנות טיוטה",
    onDemandOrders: "הזמנות מראש",
    googleSheetsSync: "חיבור Google Sheets",
    orderTopUp: "השלמת הזמנה",
    euContainerMode: "מכולה אירופית",
    mailTransport: "דואר הזמנות",
    dbHealth: "ניטור מסד נתונים",
    catalogSearch: "חיפוש מוצרים",
    wizardAnalytics: "ניתוח אשף צמיגים",
    productAnalytics: "אנליטיקת מוצרים",
    salesSummary: "סיכום מכירות ומלאי",
    performanceReports: "דוחות ביצועים",
    eceCertificates: "תעודות ECE",
    eceInvoices: "חשבוניות ECE",
    coupons: "קופונים",
    limitedTimeOffers: "מבצעים לזמן מוגבל",
    tireSets: "סטי צמיגים",
    brandLogos: "לוגואים - מותגים",
    paymentLinks: "קישורי תשלום",
    creditguardAudits: "ביקורת CreditGuard",
    homepageSalesPromo: "פרומו דף הבית",
    more: "עוד",
    analytics: "אנליטיקה",
    operations: "תפעול",
    promos: "מבצעים",
  },
  logout: "התנתק",
  orderManagement: "ניהול הזמנות",
  draftOrderManagement: "ניהול הזמנות טיוטה",
  wizardAnalyticsManagement: "ניתוח שימוש באשף בחירת צמיגים",
  productAnalyticsManagement: "אנליטיקה מתקדמת של מוצרים",
  salesSummaryManagement: "סיכום מכירות ומלאי שנתי",
  eceCertificatesManagement: "ניהול תעודות ECE",
  eceInvoicesManagement: "ניהול חשבוניות ECE",
  couponsManagement: "ניהול קופוני הנחה",
  limitedTimeOffersManagement: "ניהול מבצעים לזמן מוגבל",
  tireSetsManagement: "ניהול סטי צמיגים",
  brandLogosManagement: "ניהול לוגואים — מותגים",
  paymentLinksManagement: "ניהול קישורי תשלום",
  creditguardAuditsManagement: "ביקורת הזמנות CreditGuard",
  homepageSalesPromoManagement: "פרומו מכירות בדף הבית",
  onDemandOrderManagement: "הזמנות מראש (מלאי אירופי)",
  googleSheetsSyncManagement: "חיבור לגיליון הזמנת המכולה (Google Sheets)",
  orderTopUpManagement: "בדיקת השלמת הזמנת ספק מול מכירות",
  euContainerModeManagement: "מצב בניית מכולה אירופית",
  mailTransportManagement: "שליחת דואר הזמנות",
  dbHealthManagement: "ניטור מסד הנתונים",
  catalogSearchManagement: "חיפוש מוצרים",
  welcome: {
    title: "ברוך הבא למערכת הניהול המתקדמת של SportyShop",
    subtitle: "בחר תפריט מהשורה העליונה כדי להתחיל לעבוד",
    description: "מערכת זו מאפשרת ניהול מקיף של הזמנות קיימות והזמנות טיוטה חדשות"
  }
};

const basePath = utils.getEdgeBasePath();

const getCurrentPathSafe = () => {
  if (typeof window !== "undefined") return window.location.pathname || "/admin";
  return "/admin";
};

const getActiveAdminNavKey = (path) => {
  if (path === "/admin" || path === "/admin/") return "dashboard";
  if (path.startsWith("/admin/orders")) return "orders";
  if (path.startsWith("/admin/draft-orders")) return "draftOrders";
  if (path.startsWith("/admin/wizard-analytics")) return "wizardAnalytics";
  if (path.startsWith("/admin/product-analytics")) return "productAnalytics";
  if (path.startsWith("/admin/sales-summary")) return "salesSummary";
  if (path.startsWith("/admin/pricing-agent")) return "pricingAgent";
  if (path.startsWith("/admin/performance-reports")) return "performanceReports";
  if (path.startsWith("/admin/ece-certificates")) return "eceCertificates";
  if (path.startsWith("/admin/ece-invoices")) return "eceInvoices";
  if (path.startsWith("/admin/coupons")) return "coupons";
  if (path.startsWith("/admin/limited-time-offers")) return "limitedTimeOffers";
  if (path.startsWith("/admin/homepage-sales-promo")) return "homepageSalesPromo";
  if (path.startsWith("/admin/tire-sets")) return "tireSets";
  if (path.startsWith("/admin/georgia-pricing")) return "georgiaPricing";
  if (path.startsWith("/admin/brand-logos")) return "brandLogos";
  if (path.startsWith("/admin/payment-links")) return "paymentLinks";
  if (path.startsWith("/admin/creditguard-order-audits")) {
    return "creditguardOrderAudits";
  }
  if (path.startsWith("/admin/on-demand-orders")) return "onDemandOrders";
  if (path.startsWith("/admin/google-sheets-sync")) return "googleSheetsSync";
  if (path.startsWith("/admin/order-top-up")) return "orderTopUp";
  if (path.startsWith("/admin/eu-container-mode")) return "euContainerMode";
  if (path.startsWith("/admin/mail-transport")) return "mailTransport";
  if (path.startsWith("/admin/db-health")) return "dbHealth";
  if (path.startsWith("/admin/product-search")) return "catalogSearch";
  return "dashboard";
};

class NewAdminPage extends React.Component {
  state = {
    isAuthenticated: false,
    user: null,
    token: "",
    sessionPending: false,
    moreMenuAnchorEl: null,
  };

  componentDidMount() {
    const savedToken = localStorage.getItem("admin_token");
    if (!savedToken) return;

    this.setState({ sessionPending: true });
    fetch(`${basePath}/auth/admin/session`, {
      headers: { "x-auth-token": savedToken },
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("admin session invalid");
        return r.json();
      })
      .then((user) => {
        localStorage.setItem("admin_user", JSON.stringify(user));
        this.setState({
          isAuthenticated: true,
          user,
          token: savedToken,
          sessionPending: false,
        });
        this.props.actions.getOrders();
        this.props.actions.getDraftOrders();
      })
      .catch(() => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_user");
        this.setState({
          isAuthenticated: false,
          user: null,
          token: "",
          sessionPending: false,
        });
      });
  }

  googleResponse = (credentialResponse) => {
    // The new library provides a credential (JWT token) instead of access token
    const tokenBlob = new Blob(
      [JSON.stringify({ credential: credentialResponse.credential }, null, 2)],
      { type: "application/json" }
    );
    const options = {
      method: "POST",
      body: tokenBlob,
      mode: "cors",
      cache: "default",
    };

    fetch(`${basePath}/auth/google`, options).then((r) => {
      const token = r.headers.get("x-auth-token");
      r.json().then((user) => {
        if (token) {
          this.setState({ isAuthenticated: true, user, token });
          // Persist authentication state
          localStorage.setItem('admin_token', token);
          localStorage.setItem('admin_user', JSON.stringify(user));
          // Load both orders and draft orders
          this.props.actions.getOrders();
          this.props.actions.getDraftOrders();
        }
      });
    }).catch((error) => {
      console.error('Authentication error:', error);
    });
  };

  googleErrorResponse = () => {
    console.error('Google authentication failed');
  };

  logout = () => {
    this.setState({
      isAuthenticated: false,
      token: "",
      user: null,
      sessionPending: false,
    });
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
  };

  getUserDisplayName = () => {
    const { user } = this.state;
    if (!user) return 'Admin';
    
    // If we have a name, use it
    if (user.name) {
      return user.name;
    }
    
    // If we have an email, extract the name part before @
    if (user.email) {
      const namePart = user.email.split('@')[0];
      // Replace dots and underscores with spaces and capitalize
      return namePart
        .replace(/[._]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    }
    
    return 'Admin';
  };

  updateOrderStatus = (id, status) => {
    this.props.actions.updateStatusInflight();
    this.props.actions.updateOrderProps(id, { status });
  };

  onUpdateTrackingCode = (id, trackingCode) => {
    this.props.actions.updateStatusInflight();
    this.props.actions.updateOrderProps(id, { trackingCode });
  };

  onResendPurchaseEmails = (id) => {
    this.props.actions.updateStatusInflight();
    this.props.actions.resendPurchaseEmails(id);
  };

  getPaymentDetails = ({ orderId, token }) => {
    this.props.actions.getPaymentDetails({ orderId, token });
  };

  openMoreMenu = (event) => {
    this.setState({ moreMenuAnchorEl: event.currentTarget });
  };

  closeMoreMenu = () => {
    this.setState({ moreMenuAnchorEl: null });
  };

  navigateTo = (path) => () => {
    this.closeMoreMenu();
    this.props.history.push(path);
  };

  renderTopNavigation = () => {
    const { classes } = this.props;
    const { moreMenuAnchorEl } = this.state;
    const currentPath = getCurrentPathSafe();
    const activeKey = getActiveAdminNavKey(currentPath);
    const isMoreActive = !["dashboard", "orders", "draftOrders"].includes(activeKey);
    
    return (
      <AppBar position="static" className={classes.adminAppBar}>
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6" className={classes.title}>
            {hebrewLabels.title}
          </Typography>
          
          <div className={classes.navigation}>
            <Button
              className={`${classes.navButton} ${activeKey === "dashboard" ? classes.navButtonActive : ""}`}
              startIcon={<DashboardIcon />}
              onClick={this.navigateTo("/admin")}
            >
              {hebrewLabels.navigation.dashboard}
            </Button>
            <Button
              className={`${classes.navButton} ${activeKey === "orders" ? classes.navButtonActive : ""}`}
              startIcon={<ShoppingCartIcon />}
              onClick={this.navigateTo("/admin/orders")}
            >
              {hebrewLabels.navigation.orders}
            </Button>
            <Button
              className={`${classes.navButton} ${activeKey === "draftOrders" ? classes.navButtonActive : ""}`}
              startIcon={<DraftIcon />}
              onClick={this.navigateTo("/admin/draft-orders")}
            >
              {hebrewLabels.navigation.draftOrders}
            </Button>

            <Button
              className={`${classes.navButton} ${isMoreActive ? classes.navButtonActive : ""}`}
              endIcon={<ExpandMoreIcon />}
              onClick={this.openMoreMenu}
              aria-controls="admin-more-menu"
              aria-haspopup="true"
            >
              {hebrewLabels.navigation.more}
            </Button>

            <Menu
              id="admin-more-menu"
              anchorEl={moreMenuAnchorEl}
              keepMounted
              open={Boolean(moreMenuAnchorEl)}
              onClose={this.closeMoreMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              getContentAnchorEl={null}
            >
              <ListSubheader disableSticky>{hebrewLabels.navigation.analytics}</ListSubheader>
              <MenuItem
                selected={activeKey === "wizardAnalytics"}
                onClick={this.navigateTo("/admin/wizard-analytics")}
              >
                <TrendingUpIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.wizardAnalytics}
              </MenuItem>
              <MenuItem
                selected={activeKey === "productAnalytics"}
                onClick={this.navigateTo("/admin/product-analytics")}
              >
                <TrendingUpIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.productAnalytics}
              </MenuItem>
              <MenuItem
                selected={activeKey === "salesSummary"}
                onClick={this.navigateTo("/admin/sales-summary")}
              >
                <TrendingUpIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.salesSummary}
              </MenuItem>
              <MenuItem
                selected={activeKey === "pricingAgent"}
                onClick={this.navigateTo("/admin/pricing-agent")}
              >
                <TrendingUpIcon style={{ marginLeft: 12 }} fontSize="small" />
                סוכן תמחור
              </MenuItem>
              <MenuItem
                selected={activeKey === "performanceReports"}
                onClick={this.navigateTo("/admin/performance-reports")}
              >
                <EmailIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.performanceReports}
              </MenuItem>
              <Divider />

              <ListSubheader disableSticky>{hebrewLabels.navigation.operations}</ListSubheader>
              <MenuItem
                selected={activeKey === "catalogSearch"}
                onClick={this.navigateTo("/admin/product-search")}
              >
                <SearchIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.catalogSearch}
              </MenuItem>
              <MenuItem
                selected={activeKey === "onDemandOrders"}
                onClick={this.navigateTo("/admin/on-demand-orders")}
              >
                <DirectionsBoatIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.onDemandOrders}
              </MenuItem>
              <MenuItem
                selected={activeKey === "googleSheetsSync"}
                onClick={this.navigateTo("/admin/google-sheets-sync")}
              >
                <TableChartIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.googleSheetsSync}
              </MenuItem>
              <MenuItem
                selected={activeKey === "euContainerMode"}
                onClick={this.navigateTo("/admin/eu-container-mode")}
              >
                <DirectionsBoatIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.euContainerMode}
              </MenuItem>
              <MenuItem
                selected={activeKey === "mailTransport"}
                onClick={this.navigateTo("/admin/mail-transport")}
              >
                <EmailIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.mailTransport}
              </MenuItem>
              <MenuItem
                selected={activeKey === "dbHealth"}
                onClick={this.navigateTo("/admin/db-health")}
              >
                <SecurityIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.dbHealth}
              </MenuItem>
              <MenuItem
                selected={activeKey === "orderTopUp"}
                onClick={this.navigateTo("/admin/order-top-up")}
              >
                <PlaylistAddCheckIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.orderTopUp}
              </MenuItem>
              <MenuItem
                selected={activeKey === "paymentLinks"}
                onClick={this.navigateTo("/admin/payment-links")}
              >
                <PaymentIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.paymentLinks}
              </MenuItem>
              <MenuItem
                selected={activeKey === "creditguardOrderAudits"}
                onClick={this.navigateTo("/admin/creditguard-order-audits")}
              >
                <SecurityIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.creditguardAudits}
              </MenuItem>
              <MenuItem
                selected={activeKey === "eceCertificates"}
                onClick={this.navigateTo("/admin/ece-certificates")}
              >
                <DescriptionIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.eceCertificates}
              </MenuItem>
              <MenuItem
                selected={activeKey === "eceInvoices"}
                onClick={this.navigateTo("/admin/ece-invoices")}
              >
                <ReceiptIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.eceInvoices}
              </MenuItem>
              <Divider />

              <ListSubheader disableSticky>{hebrewLabels.navigation.promos}</ListSubheader>
              <MenuItem
                selected={activeKey === "coupons"}
                onClick={this.navigateTo("/admin/coupons")}
              >
                <LocalOfferIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.coupons}
              </MenuItem>
              <MenuItem
                selected={activeKey === "limitedTimeOffers"}
                onClick={this.navigateTo("/admin/limited-time-offers")}
              >
                <LocalOfferIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.limitedTimeOffers}
              </MenuItem>
              <MenuItem
                selected={activeKey === "homepageSalesPromo"}
                onClick={this.navigateTo("/admin/homepage-sales-promo")}
              >
                <LocalOfferIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.homepageSalesPromo}
              </MenuItem>
              <MenuItem
                selected={activeKey === "tireSets"}
                onClick={this.navigateTo("/admin/tire-sets")}
              >
                <SettingsIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.tireSets}
              </MenuItem>
              <MenuItem
                selected={activeKey === "georgiaPricing"}
                onClick={this.navigateTo("/admin/georgia-pricing")}
              >
                <SettingsIcon style={{ marginLeft: 12 }} fontSize="small" />
                מחירי גאורגיה (מלאי אירופה)
              </MenuItem>
              <MenuItem
                selected={activeKey === "brandLogos"}
                onClick={this.navigateTo("/admin/brand-logos")}
              >
                <SettingsIcon style={{ marginLeft: 12 }} fontSize="small" />
                {hebrewLabels.navigation.brandLogos}
              </MenuItem>
            </Menu>
          </div>

          <div className={classes.userSection}>
            <Typography variant="body2">
              {this.getUserDisplayName()}
            </Typography>
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              startIcon={<ExitToAppIcon />}
              onClick={this.logout}
              className={classes.logoutButton}
            >
              {hebrewLabels.logout}
            </Button>
          </div>
        </Toolbar>
      </AppBar>
    );
  };

  renderContent = () => {
    const { classes, catalog } = this.props;
    const { orders, products, during, draftOrders } = catalog.admin;
    const { _id = "" } = this.state.user || {};

    return (
      <div className={classes.content}>
        <Switch>
          <Route exact path="/admin">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.navigation.dashboard}
            </Typography>
            <AdminAnalyticsDashboard />
          </Route>
          
          <Route exact path="/admin/orders">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.orderManagement}
            </Typography>
            {orders.length > 0 ? (
              <OrderList
                during={during}
                orders={orders}
                products={products}
                token={_id}
                onGetPaymentDetails={this.getPaymentDetails}
                onUpdateStatus={this.updateOrderStatus}
                onUpdateTrackingCode={this.onUpdateTrackingCode}
                onResendPurchaseEmails={this.onResendPurchaseEmails}
              />
            ) : (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                  {during ? "טוען הזמנות..." : "לא נמצאו הזמנות"}
                </Typography>
                {!during && (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 10 }}>
                    מספר הזמנות בזיכרון: {orders.length}
                  </Typography>
                )}
              </div>
            )}
          </Route>
          
          <Route exact path="/admin/draft-orders">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.draftOrderManagement}
            </Typography>
            <DraftOrdersManager 
              draftOrders={draftOrders || []}
              actions={this.props.actions}
            />
          </Route>

          <Route exact path="/admin/wizard-analytics">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.wizardAnalyticsManagement}
            </Typography>
            <WizardAnalytics />
          </Route>

          <Route exact path="/admin/product-analytics">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.productAnalyticsManagement}
            </Typography>
            <ProductAnalyticsPage />
          </Route>

          <Route exact path="/admin/sales-summary">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.salesSummaryManagement}
            </Typography>
            <SalesStockSummary />
          </Route>

          <Route exact path="/admin/pricing-agent">
            <Typography variant="h4" className={classes.headline}>
              {"Pricing agent"}
            </Typography>
            <PricingAgentPage />
          </Route>

          <Route exact path="/admin/performance-reports">
            <Typography variant="h4" className={classes.headline}>
              דוחות ביצועים
            </Typography>
            <PerformanceReportsPage />
          </Route>

          <Route exact path="/admin/ece-certificates">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.eceCertificatesManagement}
            </Typography>
            <EceCertificatesManager />
          </Route>

          <Route exact path="/admin/ece-invoices">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.eceInvoicesManagement}
            </Typography>
            <EceInvoicesManager />
          </Route>

          <Route exact path="/admin/coupons">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.couponsManagement}
            </Typography>
            <CouponsManager />
          </Route>

          <Route exact path="/admin/limited-time-offers">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.limitedTimeOffersManagement}
            </Typography>
            <LimitedTimeOffersManager />
          </Route>

          <Route exact path="/admin/homepage-sales-promo">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.homepageSalesPromoManagement}
            </Typography>
            <HomepageSalesPromoManager />
          </Route>

          <Route exact path="/admin/tire-sets">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.tireSetsManagement}
            </Typography>
            <TireSetsManager />
          </Route>

          <Route exact path="/admin/georgia-pricing">
            <Typography variant="h4" className={classes.headline}>
              מחירי גאורגיה — מלאי אירופה
            </Typography>
            <GeorgiaPricingManager />
          </Route>

          <Route exact path="/admin/brand-logos">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.brandLogosManagement}
            </Typography>
            <BrandLogoManager />
          </Route>

          <Route exact path="/admin/payment-links">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.paymentLinksManagement}
            </Typography>
            <PaymentLinksManager />
          </Route>

          <Route exact path="/admin/creditguard-order-audits">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.creditguardAuditsManagement}
            </Typography>
            <CreditGuardOrderAuditManager catalog={catalog} />
          </Route>

          <Route exact path="/admin/product-search">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.catalogSearchManagement}
            </Typography>
            <CatalogSearch />
          </Route>
          <Route exact path="/admin/on-demand-orders">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.onDemandOrderManagement}
            </Typography>
            <OnDemandOrdersManager />
          </Route>
          <Route exact path="/admin/google-sheets-sync">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.googleSheetsSyncManagement}
            </Typography>
            <GoogleSheetsSyncManager />
          </Route>
          <Route exact path="/admin/eu-container-mode">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.euContainerModeManagement}
            </Typography>
            <EuContainerModeManager />
          </Route>
          <Route exact path="/admin/mail-transport">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.mailTransportManagement}
            </Typography>
            <MailTransportManager />
          </Route>
          <Route exact path="/admin/db-health">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.dbHealthManagement}
            </Typography>
            <DbHealthMonitor />
          </Route>
          <Route exact path="/admin/order-top-up">
            <Typography variant="h4" className={classes.headline}>
              {hebrewLabels.orderTopUpManagement}
            </Typography>
            <OrderTopUpManager />
          </Route>

          <Route path="/admin/draft-orders/:orderId">
            <DraftOrderDetailPage 
              actions={this.props.actions}
              catalog={catalog}
            />
          </Route>
          
          <Route>
            <Redirect to="/admin" />
          </Route>
        </Switch>
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const { isAuthenticated, sessionPending } = this.state;

    const content = sessionPending ? (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Typography variant="body1" color="textSecondary">
          בודק התחברות…
        </Typography>
      </div>
    ) : isAuthenticated ? (
      <div>
        {this.renderTopNavigation()}
        {this.renderContent()}
      </div>
    ) : (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Typography variant="h4" style={{ marginBottom: 20 }}>
          {hebrewLabels.title}
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 30 }}>
          נדרשת הזדהות Google כדי לגשת למערכת הניהול
        </Typography>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={this.googleResponse}
            onError={this.googleErrorResponse}
            useOneTap
            auto_select
            theme="outline"
            size="large"
            text="signin_with"
            shape="rectangular"
          />
        </div>
      </div>
    );

    // Wrap entire component with GoogleOAuthProvider at root level
    return (
      <GoogleOAuthProvider clientId="953186478491-6mps8iroc14e0h7sci1q2mh1q50dsqi6.apps.googleusercontent.com">
        <div>
          <Paper className={classes.root} elevation={4}>
            {content}
          </Paper>
        </div>
      </GoogleOAuthProvider>
    );
  }
}

NewAdminPage.propTypes = {
  classes: PropTypes.object.isRequired,
  catalog: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default withRouter(withStyles(styles)(NewAdminPage)); 